import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  investor_search,
  GetInvestorProfile,
  GetVotingRationale_byInvestor,
  listActivistFilingsByActivist_v2,
  GetFMProfile,
} from '../../utils/investor-util';
import prodConst from '../../constants/ProductConstants';
import { AllowDownload, UpdateVisitorLog, HandleTrialLog, TokenDecode } from '../../utils/general-util';
import { SEARCHLIMIT } from '../../constants/SearchConstants';

export const searchFormReq = createAsyncThunk('investor_search', async (arg) => {
  const response = await investor_search(arg.name_search, arg.quicksearch, arg.limit, arg.offset, arg.cancelToken);
  return response;
});

export const getInvestorProfileReq = createAsyncThunk('investor_profile', async (investor) => {
  const response = await GetInvestorProfile(investor);
  return response;
});

export const getTokenDecode = createAsyncThunk('getTokenDecode', async () => {
  const response = await TokenDecode();
  return response;
});

export const getlistActivistFilingsByActivist_v2Req = createAsyncThunk(
  'listCompanyFilingsByActivist_v2',
  async (arg) => {
    const response = await listActivistFilingsByActivist_v2(arg.company_id, arg.activist_id, arg.longShort);
    return response;
  },
);

export const getVotingRationale_byInvestorReq = createAsyncThunk(
  'GetVotingRationale_byInvestor',
  async (investor, { rejectWithValue }) => {
    let TrialStatus = false;
    const productId = prodConst.VOTING;

    const resTrial = await HandleTrialLog(productId); // Trial User Check
    if (resTrial !== undefined) {
      TrialStatus = resTrial.response;
    }
    const resAllowDownload = await AllowDownload(productId); // Allow Download Option
    const response = await GetVotingRationale_byInvestor(investor);

    // The util swallows API errors/timeouts and returns false (and returns
    // undefined on a non-2xx status). Both mean the load failed, so reject the
    // thunk to drive the .rejected reducer and surface the retry UI, instead of
    // silently falling through to the empty "Records not found." state.
    if (!response) {
      return rejectWithValue('VOTING_RATIONALE_LOAD_FAILED');
    }

    const responseObj = {
      response,
      trialStatus: TrialStatus,
      allowDownload: resAllowDownload,
    };
    return responseObj;
  },
);

export const GetFMProfileReq = createAsyncThunk('GetFMProfile', async (arg) => {
  const response = await GetFMProfile(arg);
  return response;
});

const InvestorSlice = createSlice({
  name: 'investor',
  initialState: {
    // General
    loadingData: true,
    DecodeToken: [],
    getTokenDecode: [],
    investorProfile: '',
    votingOverviewLoading: true,
    TrialLog_voting: false,
    searchInvestorRecordset: undefined,

    getFMProfile: undefined,
    getFMProfilePDF: [],
    // voting Rationale
    votingRationale_data: [],
    votingRationale_heading: {},
    votingRationale_trialStatus: false,
    allowDownload: false,
    isLoading: true,
    votingRationale_error: false,
    activistFilings: [],
    activistFilingsAiS: [],
    GetFMProfile: {},
    hasNoMoreRecords: false,
  },
  reducers: {
    handleVisitorLog: {
      reducer() {
        // state = action.payload;
      },
      prepare(page_name, query_string) {
        UpdateVisitorLog(page_name, query_string);
        return {
          payload: { DecodeToken: [] },
        };
      },
    },
    handleResetFiling: {
      reducer(state) {
        state.activistFilings = [];
        state.activistFilingsAiS = [];
        state.loadingData = true;
      },
      prepare() {
        return {
          payload: {},
        };
      },
    },
    resetInvestorProfile: {
      reducer(state, action) {
        state.investorProfile = action.payload;
      },
      prepare() {
        return {
          payload: {},
        };
      },
    },

    handleSelectedMeetingDates: {
      reducer(state, action) {
        state.selectedMeetingDates = action.payload.selectedMeetingDates;
      },
      prepare(selectedMeetingDates) {
        return {
          payload: { selectedMeetingDates },
        };
      },
    },

    handleresetFmProfile: {
      reducer(state) {
        state.loadingData = true;
        state.getFMProfile = undefined;
      },
      prepare() {
        return {
          payload: {},
        };
      },
    },
    handleresetLoadingData: {
      reducer(state) {
        state.loadingData = true;
      },
      prepare() {
        return {
          payload: {},
        };
      },
    },
    handleResetSearch: {
      reducer(state) {
        state.searchInvestorRecordset = undefined;
        state.hasNoMoreRecords = false;
      },
      prepare() {
        return {
          payload: {},
        };
      },
    },
    handleResetVotingrationale: {
      reducer(state) {
        state.votingRationale_data = [];
        state.votingRationale_heading = {};
        state.isLoading = true;
        state.votingRationale_error = false;
      },
      prepare() {
        return {};
      },
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchFormReq.pending, (state, action) => {
        if (!action.meta.arg.paging) {
          state.searchInvestorRecordset = undefined;
        }
        state.hasNoMoreRecords = false;
      })
      .addCase(searchFormReq.fulfilled, (state, action) => {
        if (action.payload !== undefined && action.payload.data.length < SEARCHLIMIT) {
          state.hasNoMoreRecords = true;
        }

        if (state.searchInvestorRecordset) {
          state.searchInvestorRecordset =
            action.payload !== false && action.payload !== undefined
              ? { data: [...state.searchInvestorRecordset.data, ...action.payload.data] }
              : { data: [] };
        } else {
          state.searchInvestorRecordset =
            action.payload !== false && action.payload !== undefined ? { data: action.payload.data } : { data: [] };
        }
      })
      .addCase(getInvestorProfileReq.fulfilled, (state, action) => {
        const data = action.payload?.data ?? action.payload?.responseInvestor?.data;
        state.investorProfile = data?.[0]?.investor_name ?? '';
      })
      .addCase(getVotingRationale_byInvestorReq.pending, (state) => {
        state.isLoading = true;
        state.votingRationale_error = false;
      })
      .addCase(getVotingRationale_byInvestorReq.fulfilled, (state, action) => {
        state.votingRationale_data = action.payload !== undefined ? action.payload.response.data : '';
        state.votingRationale_heading = action.payload !== undefined ? action.payload.response.heading : '';
        state.votingRationale_trialStatus = action.payload !== undefined ? action.payload.trialStatus : '';
        state.allowDownload = action.payload !== undefined ? action.payload.allowDownload : '';
        state.isLoading = action.payload === undefined;
        state.votingRationale_error = false;
      })
      .addCase(getVotingRationale_byInvestorReq.rejected, (state) => {
        state.votingRationale_data = [];
        state.votingRationale_heading = {};
        state.isLoading = false;
        state.votingRationale_error = true;
      })
      .addCase(getlistActivistFilingsByActivist_v2Req.fulfilled, (state, action) => {
        if (action.payload) {
          state.activistFilings = action.payload && action.payload instanceof Array ? action.payload : [];
        }
        state.loadingData = action.payload === undefined;
      })
      .addCase(GetFMProfileReq.fulfilled, (state, action) => {
        state.getFMProfile = action.payload !== undefined ? action.payload.data : undefined;
        state.getFMProfilePDF = action.payload ? action.payload.newarray : [];
      });
  },
});

export const {
  handleVisitorLog,
  resetInvestorProfile,
  handleSelectedMeetingDates,
  handleResetFiling,
  handleresetFmProfile,
  handleresetLoadingData,
  handleResetSearch,
  handleResetVotingrationale,
} = InvestorSlice.actions;

export default InvestorSlice.reducer;
